import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiCall } from '../utils/api';
import { toast } from '../components/ui/sonner';
import { Briefcase, Mail, Lock, ArrowRight, Eye, EyeOff, Activity, Users, Award, X, ShieldQuestion, KeyRound } from 'lucide-react';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState(null); // {token, method, email}
  const [mfaCode, setMfaCode] = useState('');
  const [expiredReason, setExpiredReason] = useState(null);

  // Pre-fill email from ?email= query param (e.g. after registration redirect).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get('email');
    if (emailParam) {
      setFormData((prev) => ({ ...prev, email: emailParam }));
    }
    const expired = params.get('expired');
    if (expired) setExpiredReason(expired);
  }, []);
  
  // Forgot password state
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotStep, setForgotStep] = useState(1);
  const [forgotEmail, setForgotEmail] = useState('');
  const [securityQuestion, setSecurityQuestion] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [hasSecurityQuestion, setHasSecurityQuestion] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  const completeLogin = (response) => {
    login(response.token, response.user);
    toast.success('Login successful!');

    const role = response.user.role;
    const employerRole = response.user.employer_role;

    if (role === 'worker') navigate('/worker');
    else if (role === 'employer') {
      // Route program managers and location managers to PM Dashboard
      if (employerRole === 'program_manager' || employerRole === 'location_manager') {
        navigate('/pm-dashboard');
      } else {
        navigate('/employer');
      }
    }
    else if (role === 'agent') navigate('/agent');
    else if (role === 'hr') navigate('/hr');
    else if (role === 'admin') navigate('/admin');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    try {
      const response = await apiCall('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify(formData),
      });

      if (response.mfa_required) {
        setMfaChallenge({ token: response.mfa_token, method: response.mfa_method, email: response.email });
        if (response.mfa_method === 'email') {
          apiCall('/api/auth/otp/send', { method: 'POST', body: JSON.stringify({ email: response.email }) })
            .then(() => toast.success('Verification code sent to your email'))
            .catch(() => {});
        }
        return;
      }

      completeLogin(response);
    } catch (error) {
      // Friendly handling for unverified email — auto-route to verification.
      // VerifyEmailPage auto-sends the OTP on mount; the backend dedupes within
      // a 60-second window, so we don't pre-send here.
      if (error.message && /verify your email/i.test(error.message)) {
        toast.error('Please verify your email to sign in. Sending you a code…');
        navigate(`/verify-email?email=${encodeURIComponent(formData.email)}`);
      } else {
        toast.error(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await apiCall('/api/auth/login/mfa', {
        method: 'POST',
        body: JSON.stringify({ mfa_token: mfaChallenge.token, code: mfaCode }),
      });
      completeLogin(response);
    } catch (error) {
      toast.error(error.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail) {
      toast.error('Please enter your email address');
      return;
    }
    
    setForgotLoading(true);
    try {
      const response = await apiCall('/api/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email: forgotEmail }),
      });
      
      if (response.has_security_question) {
        setHasSecurityQuestion(true);
        setSecurityQuestion(response.security_question);
        setForgotStep(2);
        toast.success('Please answer your security question');
      } else {
        setHasSecurityQuestion(false);
        setForgotStep(2);
        toast.success(response.message);
        if (response.debug_token) {
          setResetToken(response.debug_token);
          toast.info(`Debug OTP: ${response.debug_token}`);
        }
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setForgotLoading(false);
    }
  };

  const handleVerifySecurityAnswer = async () => {
    if (!securityAnswer) {
      toast.error('Please enter your security answer');
      return;
    }
    
    setForgotLoading(true);
    try {
      const response = await apiCall('/api/auth/verify-security-answer', {
        method: 'POST',
        body: JSON.stringify({ email: forgotEmail, security_answer: securityAnswer }),
      });
      
      setResetToken(response.reset_token);
      setForgotStep(3);
      toast.success('Security answer verified! Set your new password.');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || !confirmNewPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setForgotLoading(true);
    try {
      await apiCall('/api/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ email: forgotEmail, reset_token: resetToken, new_password: newPassword }),
      });
      
      toast.success('Password reset successfully! Please login.');
      closeForgotModal();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setForgotLoading(false);
    }
  };

  const closeForgotModal = () => {
    setShowForgotModal(false);
    setForgotStep(1);
    setForgotEmail('');
    setSecurityQuestion('');
    setSecurityAnswer('');
    setResetToken('');
    setNewPassword('');
    setConfirmNewPassword('');
    setHasSecurityQuestion(false);
  };

  return (
    <div className="min-h-screen bg-slate-950 flex">
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-2.5 mb-10">
            <div className="w-10 h-10 rounded-xl bg-cyan-400 flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-slate-950" />
            </div>
            <span className="text-xl font-bold text-white font-['Manrope']">
              Scensob<span className="text-cyan-400">Connect</span>
            </span>
          </div>
          
          {/* Form Header */}
          <div className="mb-8">
            <h2 className="text-2xl lg:text-3xl font-bold text-white mb-2 font-['Manrope']">Welcome back</h2>
            <p className="text-slate-500">
              Don't have an account?{' '}
              <button 
                onClick={() => navigate('/register')} 
                className="text-cyan-400 font-semibold hover:text-cyan-300 transition-colors"
              >
                Sign up free
              </button>
            </p>
          </div>
          
          {/* Login Form */}
          {expiredReason && (
            <div className="mb-4 px-4 py-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-sm" data-testid="session-expired-banner">
              {expiredReason === 'idle'
                ? 'Your session has expired due to inactivity.'
                : 'For your security, your session has expired. Please sign in again.'}
            </div>
          )}
          {mfaChallenge ? (
            <form onSubmit={handleMfaSubmit} className="space-y-5" data-testid="mfa-login-form">
              <div className="text-center">
                <h2 className="text-lg font-semibold text-white">Two-factor verification</h2>
                <p className="text-sm text-slate-400 mt-1">
                  {mfaChallenge.method === 'totp'
                    ? 'Enter the 6-digit code from your authenticator app.'
                    : `Enter the 6-digit code we emailed to ${mfaChallenge.email}.`}
                </p>
              </div>
              <input
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                inputMode="numeric"
                autoFocus
                className="w-full h-12 px-4 bg-slate-950 border border-slate-800 rounded-lg text-white text-center tracking-[0.5em] focus:outline-none focus:border-cyan-500"
                data-testid="mfa-code-input"
              />
              <button
                type="submit"
                disabled={loading || mfaCode.length < 6}
                className="w-full h-12 rounded-full bg-cyan-500 text-white font-semibold hover:bg-cyan-600 transition-colors disabled:opacity-40"
                data-testid="mfa-submit-btn"
              >
                {loading ? 'Verifying…' : 'Verify & Sign In'}
              </button>
              <button type="button" onClick={() => { setMfaChallenge(null); setMfaCode(''); }}
                      className="w-full text-sm text-slate-400 hover:text-white">
                Back to login
              </button>
            </form>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-2">Email address</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Mail className="w-5 h-5 text-slate-600" />
                </div>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full h-12 pl-12 pr-4 bg-slate-950 border border-slate-800 rounded-lg text-white placeholder-slate-600 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
                  placeholder="you@company.com"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-400 mb-2">Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="w-5 h-5 text-slate-600" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full h-12 pl-12 pr-12 bg-slate-950 border border-slate-800 rounded-lg text-white placeholder-slate-600 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
                  placeholder="Enter your password"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5 text-slate-600 hover:text-slate-400 transition-colors" />
                  ) : (
                    <Eye className="w-5 h-5 text-slate-600 hover:text-slate-400 transition-colors" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 rounded border-slate-700 bg-slate-900 text-cyan-500 focus:ring-cyan-500" />
                <span className="text-sm text-slate-500">Remember me</span>
              </label>
              <button 
                type="button" 
                onClick={() => setShowForgotModal(true)}
                className="text-sm font-medium text-cyan-400 hover:text-cyan-300 transition-colors"
                data-testid="forgot-password-btn"
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="group w-full h-12 bg-cyan-400 text-slate-950 rounded-full font-semibold hover:bg-cyan-300 shadow-[0_0_15px_rgba(34,211,238,0.3)] hover:shadow-[0_0_25px_rgba(34,211,238,0.4)] transition-all hover:scale-[1.02] active:scale-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
              data-testid="login-submit-button"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-slate-950/30 border-t-slate-950 rounded-full animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  Sign In
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>
          )}

          {/* Footer */}
          <p className="mt-8 text-center text-xs text-slate-600">
            By signing in, you agree to our{' '}
            <a href="#" className="text-slate-500 hover:text-cyan-400 transition-colors">Terms of Service</a>
            {' '}and{' '}
            <a href="#" className="text-slate-500 hover:text-cyan-400 transition-colors">Privacy Policy</a>
          </p>
        </div>
      </div>
      
      {/* Right Side - Image & Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Background Image */}
        <img 
          src="https://images.unsplash.com/photo-1579684385127-1ef15d508118?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Healthcare team"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Cyan Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 via-slate-950/70 to-slate-950/90" />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-end p-12 lg:p-16">
          <h1 className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-6 font-['Manrope']">
            On-demand staffing,<br />
            <span className="text-cyan-400">simplified.</span>
          </h1>
          
          <p className="text-lg text-slate-300 leading-relaxed mb-8 max-w-md">
            Connect with verified workers or find your next job across multiple industries in seconds.
          </p>
          
          {/* Stats */}
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-cyan-400/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">10K+</div>
                <div className="text-xs text-slate-400">Active Workers</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-cyan-400/20 flex items-center justify-center">
                <Activity className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">50K+</div>
                <div className="text-xs text-slate-400">Jobs Filled</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-cyan-400/20 flex items-center justify-center">
                <Award className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">99%</div>
                <div className="text-xs text-slate-400">Satisfaction</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between p-6 border-b border-slate-800">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-cyan-400/20 rounded-lg">
                  <KeyRound className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Reset Password</h3>
                  <p className="text-sm text-slate-500">
                    {forgotStep === 1 && 'Enter your email to continue'}
                    {forgotStep === 2 && (hasSecurityQuestion ? 'Answer your security question' : 'Enter the code sent to your email')}
                    {forgotStep === 3 && 'Create a new password'}
                  </p>
                </div>
              </div>
              <button onClick={closeForgotModal} className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {forgotStep === 1 && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-2">Email address</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Mail className="w-5 h-5 text-slate-600" />
                      </div>
                      <input
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        className="w-full h-12 pl-12 pr-4 bg-slate-950 border border-slate-800 rounded-lg text-white placeholder-slate-600 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
                        placeholder="you@company.com"
                        data-testid="forgot-email-input"
                      />
                    </div>
                  </div>
                  <button
                    onClick={handleForgotPassword}
                    disabled={forgotLoading}
                    className="w-full h-12 bg-cyan-400 text-slate-950 rounded-full font-semibold hover:bg-cyan-300 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="forgot-continue-btn"
                  >
                    {forgotLoading ? <div className="w-5 h-5 border-2 border-slate-950/30 border-t-slate-950 rounded-full animate-spin" /> : <>Continue<ArrowRight className="w-5 h-5" /></>}
                  </button>
                </>
              )}

              {forgotStep === 2 && (
                <>
                  {hasSecurityQuestion ? (
                    <>
                      <div className="bg-slate-800/50 rounded-lg p-4 mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <ShieldQuestion className="w-4 h-4 text-cyan-400" />
                          <span className="text-sm font-medium text-slate-300">Security Question</span>
                        </div>
                        <p className="text-white">{securityQuestion}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-400 mb-2">Your Answer</label>
                        <input
                          type="text"
                          value={securityAnswer}
                          onChange={(e) => setSecurityAnswer(e.target.value)}
                          className="w-full h-12 px-4 bg-slate-950 border border-slate-800 rounded-lg text-white placeholder-slate-600 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
                          placeholder="Enter your answer"
                          data-testid="security-answer-input"
                        />
                      </div>
                      <button
                        onClick={handleVerifySecurityAnswer}
                        disabled={forgotLoading}
                        className="w-full h-12 bg-cyan-400 text-slate-950 rounded-full font-semibold hover:bg-cyan-300 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                        data-testid="verify-answer-btn"
                      >
                        {forgotLoading ? <div className="w-5 h-5 border-2 border-slate-950/30 border-t-slate-950 rounded-full animate-spin" /> : <>Verify Answer<ArrowRight className="w-5 h-5" /></>}
                      </button>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-slate-400 mb-2">Reset Code</label>
                        <input
                          type="text"
                          value={resetToken}
                          onChange={(e) => setResetToken(e.target.value)}
                          className="w-full h-12 px-4 bg-slate-950 border border-slate-800 rounded-lg text-white placeholder-slate-600 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
                          placeholder="Enter the code from your email"
                          data-testid="reset-code-input"
                        />
                      </div>
                      <button
                        onClick={() => setForgotStep(3)}
                        disabled={!resetToken}
                        className="w-full h-12 bg-cyan-400 text-slate-950 rounded-full font-semibold hover:bg-cyan-300 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        Continue<ArrowRight className="w-5 h-5" />
                      </button>
                    </>
                  )}
                </>
              )}

              {forgotStep === 3 && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-2">New Password</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Lock className="w-5 h-5 text-slate-600" />
                      </div>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full h-12 pl-12 pr-12 bg-slate-950 border border-slate-800 rounded-lg text-white placeholder-slate-600 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
                        placeholder="Enter new password"
                        data-testid="new-password-input"
                      />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-4 flex items-center">
                        {showPassword ? <EyeOff className="w-5 h-5 text-slate-600 hover:text-slate-400" /> : <Eye className="w-5 h-5 text-slate-600 hover:text-slate-400" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-2">Confirm New Password</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Lock className="w-5 h-5 text-slate-600" />
                      </div>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        className="w-full h-12 pl-12 pr-4 bg-slate-950 border border-slate-800 rounded-lg text-white placeholder-slate-600 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
                        placeholder="Confirm new password"
                        data-testid="confirm-new-password-input"
                      />
                    </div>
                  </div>
                  <button
                    onClick={handleResetPassword}
                    disabled={forgotLoading}
                    className="w-full h-12 bg-cyan-400 text-slate-950 rounded-full font-semibold hover:bg-cyan-300 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="reset-password-btn"
                  >
                    {forgotLoading ? <div className="w-5 h-5 border-2 border-slate-950/30 border-t-slate-950 rounded-full animate-spin" /> : 'Reset Password'}
                  </button>
                </>
              )}

              {forgotStep > 1 && (
                <button onClick={() => setForgotStep(forgotStep - 1)} className="w-full text-sm text-slate-500 hover:text-slate-400 transition-colors">
                  ← Go back
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
